// Shared English word validator for word-search style games (WordPool, Boggle).
// Rejects non-words AND proper nouns (names, cities, countries, rivers, etc.).
//
// Strategy: Wiktionary REST is authoritative — a 200 with an `en` section
// containing non-proper-noun English entries means it's a real word; a 404
// means it isn't. Datamuse is a fallback used only when Wiktionary is
// unreachable, because Datamuse's wordlist contains archaic/rare entries like
// 'stic' tagged as common nouns that would otherwise pass. If both are
// unreachable, fail open so a flaky network doesn't block the student.

const normalize = (w) => (w || '').toUpperCase().replace(/[^A-Z]/gi, '');

const dictCache = new Map();

const REQUEST_TIMEOUT_MS = 4000;
const DATAMUSE_FREQ_FLOOR = 1.0;

const fetchWithTimeout = async (url, ms) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
};

// Returns 'valid' | 'invalid' | 'unknown'
// Wiktionary returns 404 for words it doesn't have — definitive invalid.
// Non-404 errors (5xx, network) mean the API is unavailable → unknown.
// A 200 is only "valid" if the `en` section has at least one entry whose
// `language` is "English" and whose `partOfSpeech` is not "Proper noun"
// (filters Translingual symbols like `run` = ISO 639 and city/person names).
const queryWiktionary = async (w) => {
  try {
    const res = await fetchWithTimeout(
      `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(w)}`,
      REQUEST_TIMEOUT_MS
    );
    if (res.status === 404) return 'invalid';
    if (!res.ok) return 'unknown';
    const data = await res.json().catch(() => null);
    const en = data && Array.isArray(data.en) ? data.en : [];
    const realEnglish = en.filter(
      (e) =>
        (e.language || '') === 'English' &&
        !/^proper\s*noun$/i.test(e.partOfSpeech || '')
    );
    return realEnglish.length > 0 ? 'valid' : 'invalid';
  } catch (_) {
    return 'unknown';
  }
};

// Returns 'valid' | 'invalid' | 'unknown'
const queryDatamuse = async (w) => {
  try {
    const res = await fetchWithTimeout(
      `https://api.datamuse.com/words?sp=${encodeURIComponent(w)}&max=1&md=fp`,
      REQUEST_TIMEOUT_MS
    );
    if (!res.ok) return 'unknown';
    const data = await res.json();
    const top = Array.isArray(data) && data[0];
    if (!top || (top.word || '').toLowerCase() !== w) return 'invalid';
    const tags = top.tags || [];
    const posTags = tags
      .filter((t) => typeof t === 'string' && !t.startsWith('f:'))
      .map((t) => t.toLowerCase());
    const hasCommonPos = posTags.some((t) => ['n', 'v', 'adj', 'adv'].includes(t));
    const hasProp = posTags.includes('prop');
    const freqTag = tags.find((t) => typeof t === 'string' && t.startsWith('f:'));
    const freq = freqTag ? parseFloat(freqTag.slice(2)) : 0;
    if (hasProp && !hasCommonPos) return 'invalid';
    if (hasProp && hasCommonPos) return freq >= DATAMUSE_FREQ_FLOOR ? 'valid' : 'invalid';
    if (hasCommonPos) return 'valid';
    return freq >= DATAMUSE_FREQ_FLOOR ? 'valid' : 'invalid';
  } catch (_) {
    return 'unknown';
  }
};

export const checkEnglishWord = async (word) => {
  const w = normalize(word).toLowerCase();
  if (!w || w.length < 2) return false;
  if (dictCache.has(w)) return dictCache.get(w);

  // Wiktionary is authoritative when it responds — 200 w/ English entries =
  // valid, 404 = invalid. Only when it's unreachable (timeout / 5xx) do we
  // fall back to Datamuse.
  const wiktVerdict = await queryWiktionary(w);
  if (wiktVerdict === 'valid') { dictCache.set(w, true); return true; }
  if (wiktVerdict === 'invalid') { dictCache.set(w, false); return false; }

  const dmVerdict = await queryDatamuse(w);
  if (dmVerdict === 'valid') { dictCache.set(w, true); return true; }
  if (dmVerdict === 'invalid') { dictCache.set(w, false); return false; }

  // Both APIs unreachable — don't block the student. Don't cache so we retry.
  return true;
};
